import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import Reveal from '@/components/base/Reveal';
import SectionHeader from '@/components/base/SectionHeader';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import {
  allGenres,
  getFeaturedMovies,
  getLatestMovies,
  getPopularMovies,
  getMoviesByIds,
  allCollections,
} from '@/utils/movie';

const stats = [
  { value: '3,200+', label: '收录影片' },
  { value: '12', label: '类型分类' },
  { value: '98%', label: '用户好评' },
  { value: '7×24', label: '在线观看' },
];

export default function Home() {
  const featured = getFeaturedMovies(5);
  const latest = getLatestMovies(8);
  const popular = getPopularMovies(6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <HeroCarousel movies={featured} />

      <main className="pb-8">
        {/* 分类快捷入口 */}
        <section className="container-page -mt-2 pt-14">
          <SectionHeader
            title="电影分类"
            subtitle="按类型快速找到你想看的好片"
            icon="ri-apps-2-line"
          />
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {allGenres.map((genre, i) => (
              <Reveal key={genre.key} delay={i * 40}>
                <Link
                  to={`/category/${genre.key}`}
                  className="group flex flex-col items-center gap-3 rounded-lg border border-background-300/50 bg-background-100 px-3 py-5 transition-colors hover:border-primary-500/60 hover:bg-background-200"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-500/15 text-2xl text-primary-400 transition-colors group-hover:bg-primary-500 group-hover:text-foreground-950">
                    <i className={genre.icon} aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold text-foreground-200 transition-colors group-hover:text-foreground-50">
                    {genre.name}
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>

        {/* 精选 / 最新上线 */}
        <section className="container-page pt-16">
          <SectionHeader
            title="精选电影"
            subtitle="最新上线与口碑佳作，一网打尽"
            icon="ri-star-smile-line"
            moreTo="/category/action"
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
            {latest.map((movie, i) => (
              <Reveal key={movie.id} delay={(i % 4) * 60}>
                <MovieCard movie={movie} />
              </Reveal>
            ))}
          </div>
        </section>

        {/* 热门推荐 */}
        <section className="container-page pt-16">
          <SectionHeader
            title="热门推荐"
            subtitle="本周影迷都在看的高人气影片"
            icon="ri-fire-line"
          />
          <div className="no-scrollbar -mx-4 flex gap-4 overflow-x-auto px-4 pb-2 md:mx-0 md:px-0">
            {popular.map((movie) => (
              <div key={movie.id} className="w-[150px] shrink-0 sm:w-[170px]">
                <MovieCard movie={movie} />
              </div>
            ))}
          </div>
        </section>

        {/* 主题专题推荐 */}
        <section className="container-page pt-16">
          <SectionHeader
            title="主题专题推荐"
            subtitle="编辑精心策划的观影合集"
            icon="ri-bookmark-3-line"
          />
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {allCollections.map((collection, i) => {
              const list = getMoviesByIds(collection.movieIds);
              return (
                <Reveal key={collection.key} delay={i * 80}>
                  <div className="h-full overflow-hidden rounded-lg border border-background-300/50 bg-background-100">
                    <Link
                      to={`/detail/${list[0]?.id ?? ''}`}
                      className="group relative block h-40 w-full overflow-hidden"
                      aria-label={`${collection.title}专题`}
                    >
                      <img
                        src={collection.cover}
                        alt={`${collection.title}专题封面`}
                        title={`${collection.title} 观影合集`}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/50 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-4">
                        <div className="flex items-center gap-2">
                          <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-bold text-foreground-950">
                            专题
                          </span>
                          <span className="text-xs text-foreground-300">
                            共 {list.length} 部
                          </span>
                        </div>
                        <h3 className="mt-2 font-heading text-lg font-extrabold text-foreground-50">
                          {collection.title}
                        </h3>
                      </div>
                    </Link>
                    <div className="p-4">
                      <p className="line-clamp-2 text-sm text-foreground-400">{collection.subtitle}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {list.slice(0, 3).map((m) => (
                          <Link
                            key={m.id}
                            to={`/detail/${m.id}`}
                            className="rounded-md border border-background-300/60 bg-background-200/60 px-2.5 py-1 text-xs text-foreground-300 transition-colors hover:border-primary-400 hover:text-primary-300"
                          >
                            {m.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* 网站简介 */}
        <section className="container-page pt-16">
          <Reveal>
            <div className="overflow-hidden rounded-lg border border-background-300/50 bg-secondary-950">
              <div className="grid grid-cols-1 gap-8 p-8 md:p-12 lg:grid-cols-2 lg:items-center">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary-400">
                    About 免费看大片
                  </span>
                  <h2 className="mt-3 font-heading text-2xl font-extrabold leading-snug text-secondary-50 md:text-3xl">
                    为每一位影迷，找到值得观看的好电影
                  </h2>
                  <p className="mt-4 max-w-lg text-sm leading-relaxed text-secondary-300 md:text-base">
                    免费看大片是一个专注于电影内容聚合与推荐的影视门户。我们汇整热门大片与经典佳作，提供类型筛选、影片资讯与个性化推荐，帮助你用最短时间发现真正想看的电影。站内播放为前端演示，资讯内容均为示例数据。
                  </p>
                  <Link
                    to="/about"
                    className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-400"
                  >
                    了解更多
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((s) => (
                    <div
                      key={s.label}
                      className="rounded-lg border border-secondary-800 bg-secondary-900/60 p-5"
                    >
                      <p className="font-heading text-2xl font-extrabold text-accent-400 md:text-3xl">
                        {s.value}
                      </p>
                      <p className="mt-1 text-sm text-secondary-300">{s.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <Footer />
    </div>
  );
}